import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import {
  addReport,
  subscribeToReports,
  addPatient,
  subscribeToPatients,
  deletePatient,
  saveReportOffline,
  getOfflineReports,
  syncOfflineReports,
  addBill,
  subscribeToBills,
  type ReportData,
  type Patient,
} from '../services/firebaseDatabase';
import type { Bill } from '../types/types';
import { useAuth } from './AuthContext';
import { toast } from 'sonner';

interface DatabaseContextType {
  // Reports
  reports: ReportData[];
  addReport: (report: Omit<ReportData, 'id'>) => Promise<void>;

  // Patients  
  patients: Patient[];
  addPatient: (patient: Omit<Patient, 'id'>) => Promise<string>;
  deletePatient?: (patientId: string) => Promise<void>;

  // Bills
  bills: Bill[];
  addBill: (bill: any) => Promise<void>;

  // State
  loading: boolean;
  syncing: boolean;

  // Offline functionality
  offlineReports: ReportData[];
  syncOfflineData: () => Promise<void>;
}

const DatabaseContext = createContext<DatabaseContextType | null>(null);

// eslint-disable-next-line react-refresh/only-export-components
export const useDatabase = () => {
  const context = useContext(DatabaseContext);
  if (!context) {
    throw new Error('useDatabase must be used within a DatabaseProvider');
  }
  return context;
};

interface DatabaseProviderProps {
  children: React.ReactNode;
}

export const DatabaseProvider: React.FC<DatabaseProviderProps> = ({ children }) => {
  const { user, isOnline, currentLab } = useAuth();
  const [reports, setReports] = useState<ReportData[]>([]);
  const [patients, setPatients] = useState<Patient[]>([]);
  const [bills, setBills] = useState<Bill[]>([]);
  const [offlineReports, setOfflineReports] = useState<ReportData[]>([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);

  // Load offline reports on mount
  useEffect(() => {
    const offline = getOfflineReports();
    setOfflineReports(offline);
  }, []);

  // Subscribe to real-time data when user is authenticated and online
  useEffect(() => {
    if (!user || !isOnline || !currentLab) {
      setLoading(false);
      return;
    }

    setLoading(true);

    // Subscribe to reports
    const unsubscribeReports = subscribeToReports((reportsData) => {
      setReports(reportsData);
      setLoading(false);
    }, currentLab.id, 50);

    // Subscribe to patients
    const unsubscribePatients = subscribeToPatients((patientsData) => {
      setPatients(patientsData);
    }, currentLab.id, 50);

    // Subscribe to bills
    const unsubscribeBills = subscribeToBills((billsData) => {
      setBills(billsData);
    }, currentLab.id, 50);

    return () => {
      unsubscribeReports();
      unsubscribePatients();
      unsubscribeBills();
    };
  }, [user, isOnline, currentLab]);

  // Prepare sync function (memoized) so effects can depend on it safely
  const syncOfflineData = useCallback(async (): Promise<void> => {
    if (!user || !isOnline || offlineReports.length === 0) {
      return;
    }

    setSyncing(true);
    const count = offlineReports.length;
    try {
      await syncOfflineReports();
      setOfflineReports([]);
      toast.success(`Synced ${count} offline report${count > 1 ? 's' : ''}`);
    } catch (error) {
      console.error('Error syncing offline data:', error);
      toast.error('Failed to sync offline data');
    } finally {
      setSyncing(false);
    }
  }, [user, isOnline, offlineReports.length]);

  // Sync offline data when coming back online
  useEffect(() => {
    if (isOnline && user && offlineReports.length > 0) {
      syncOfflineData();
    }
  }, [isOnline, user, offlineReports.length, syncOfflineData]);

  const handleAddReport = async (reportData: Omit<ReportData, 'id'>): Promise<void> => {
    try {
      if (!user) {
        throw new Error('User must be authenticated');
      }

      if (!currentLab) {
        throw new Error('No active Lab found');
      }

      const report = {
        ...reportData,
        patientInfo: reportData.patientInfo,
        createdBy: user.email || 'Unknown User',
        labId: currentLab.id
      };

      if (isOnline) {
        // Add to Firestore when online
        await addReport(report);
        toast.success('Report saved successfully');
      } else {
        // Save offline when no internet
        saveReportOffline(report);
        const updated = getOfflineReports();
        setOfflineReports(updated);
        toast.success('Report saved offline - will sync when online');
      }
    } catch (error) {
      console.error('Error adding report:', error);

      // Fallback to offline storage if Firestore fails
      saveReportOffline({
        ...reportData,
        patientInfo: reportData.patientInfo,
        createdBy: user?.email || 'Unknown User',
        labId: currentLab?.id // Might be undefined if offline and context lost, but usually cached
      });
      const updated = getOfflineReports();
      setOfflineReports(updated);

      toast.error('Saved offline - will sync when connection is restored');
    }
  };

  const handleAddPatient = async (patientData: Omit<Patient, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> => {
    try {
      if (!user) {
        throw new Error('User must be authenticated');
      }

      if (!isOnline) {
        throw new Error('Patient registration requires internet connection');
      }

      if (!currentLab) {
        throw new Error('No active Lab found');
      }

      const patientId = await addPatient({
        ...patientData,
        labId: currentLab.id
      });
      // Toast handled by calling component
      return patientId;
    } catch (error) {
      console.error('Error adding patient:', error);
      toast.error('Failed to register patient');
      throw error;
    }
  };



  interface FirebaseTimestamp {
    seconds?: number;
    nanoseconds?: number;
  }

  // Combine online and offline reports for display
  const allReports = [...reports, ...offlineReports].sort((a, b) => {
    const dateA = a.createdAt ? (typeof a.createdAt === 'string' ? new Date(a.createdAt) :
      (a.createdAt instanceof Date ? a.createdAt :
        (typeof a.createdAt === 'object' && (a.createdAt as FirebaseTimestamp)?.seconds ?
          new Date((a.createdAt as FirebaseTimestamp).seconds! * 1000) : new Date()))) : new Date();
    const dateB = b.createdAt ? (typeof b.createdAt === 'string' ? new Date(b.createdAt) :
      (b.createdAt instanceof Date ? b.createdAt :
        (typeof b.createdAt === 'object' && (b.createdAt as FirebaseTimestamp)?.seconds ?
          new Date((b.createdAt as FirebaseTimestamp).seconds! * 1000) : new Date()))) : new Date();
    return dateB.getTime() - dateA.getTime();
  });

  const value: DatabaseContextType = {
    reports: allReports,
    addReport: handleAddReport,
    patients,
    addPatient: handleAddPatient,
    deletePatient: async (patientId: string) => {
      try {
        if (!user) throw new Error('Authentication required');
        await deletePatient(patientId);
        toast.success('Patient deleted');
        // remove locally for immediate UI feedback
        setPatients(prev => prev.filter(p => p.id !== patientId));
      } catch (error) {
        console.error('Error deleting patient from context:', error);
        toast.error('Failed to delete patient');
        throw error;
      }
    },
    // Bills
    bills,
    addBill: async (billData: any) => {
      try {
        if (!user || !currentLab) throw new Error('Authentication required');
        await addBill({ ...billData, labId: currentLab.id });
        toast.success('Bill saved successfully');
      } catch (error) {
        console.error('Error adding bill:', error);
        toast.error('Failed to save bill');
        throw error;
      }
    },
    loading,
    syncing,
    offlineReports,
    syncOfflineData
  };

  return (
    <DatabaseContext.Provider value={value}>
      {children}
    </DatabaseContext.Provider>
  );
};